import React, {useCallback, useState} from 'react';
import {getDateByCustomFormat, DATE_FORMATS} from 'utils/date';
import {useUser} from 'common/stores/user';
import Notes from './Notes';

import {Button, Divider, Layout, Text, TextArea, TextField} from '@phonectas/unifon-ui-kit';
import {StyledDivider} from 'common/components/styled';
import {useTranslation} from 'react-i18next';
import {StyledNoSelectText} from 'common/styled';
import TextDivWithLabel from 'common/components/TextDivWithLabel';
import EmailNotificationTable from '../caseEmailNotification/EmailNotificationTable';
import CaseAttachments from '../caseAttachments/CaseAttachments';
import {unifonEmailValid} from 'utils/email';
import useBmoStore from 'common/stores/bmoStore';
import {BmoTicketsList, TicketResponse} from 'common/types/store/bmoTypes';
import Accordion from 'common/components/Accordion';

const MAX_NOTIFICATION_EMAILS = 10;

interface CaseContentProps {
	currentTicket: TicketResponse | null;
	noteMsg: string;
	handleNoteChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
	handleAddNote: () => void;
	ticketList: BmoTicketsList | null;
	show?: boolean;
	onClose?: () => void;
}

const CaseContent: React.FC<CaseContentProps> = ({
	currentTicket,
	noteMsg,
	handleNoteChange,
	handleAddNote,
	ticketList,
}) => {
	const [t] = useTranslation();
	const isUser = useUser()?.catalogID;
	const addEmail = useBmoStore((state) => state.addEmail);
	const getTicketId = useBmoStore((state) => state.getTicketId);
	const isNotes = (currentTicket?.note?.length ?? 0) > 0;
	const attachments = currentTicket?.attachment;
	const attachmentSize = attachments?.length ?? 0;
	const [email, setEmail] = useState('');
	const [noteAccordion, setNoteAccordion] = useState(false);
	const [emailAccordion, setEmailAccordion] = useState(false);
	const [attachmentAccordion, setAttachmentAccordion] = useState(false);
	const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setEmail(e.target.value);
	}, []);
	const notificationEmails = currentTicket?.notificationEmails;
	// Same rule as the create form: a unifon.no address, no duplicates, max 10 (backend limit).
	const validateEmail = useCallback((emailToValidate: string) => {
		const existing = (notificationEmails ?? []).map((e) => e.trim().toLowerCase());
		return unifonEmailValid(emailToValidate.trim()) &&
			!existing.includes(emailToValidate.trim().toLowerCase()) &&
			existing.length < MAX_NOTIFICATION_EMAILS;
	}, [notificationEmails]);

	const toggleEmailAccordion = useCallback(() => {
		setEmailAccordion((prev) => !prev);
	}, []);
	const toggleAttachmentAccordion = useCallback(() => {
		setAttachmentAccordion((prev) => !prev);
	}, []);
	const toggleNoteAccordion = useCallback(() => {
		setNoteAccordion((prev) => !prev);
	}, []);

	const handleDeleteEmail = useCallback((index: number) => {
		const ticketId = currentTicket?.id;
		if (!ticketId) return;
		const updatedEmails = (currentTicket?.notificationEmails ?? []).toSpliced(index, 1);
		addEmail(
			ticketId,
			updatedEmails,
			() => {
				setEmail('');
				getTicketId(ticketId);
			}
		);
	}, [currentTicket, addEmail, getTicketId]);

	const handleAddEmail = useCallback(() => {
		const ticketId = currentTicket?.id;
		if (!ticketId) return;
		if (email && validateEmail(email)) {
			const updatedEmails = [...(currentTicket?.notificationEmails ?? []), email.trim()];
			addEmail(
				ticketId,
				updatedEmails,
				() => {
					setEmail('');
					getTicketId(ticketId);
				}
			);
		}
	}, [
		addEmail,
		currentTicket?.id,
		email,
		getTicketId,
		validateEmail,
		currentTicket?.notificationEmails,
	]);

	return (
		<Layout>
			<Divider style={{marginBottom: '10px'}}/>
			<Layout direction="column" gap="xs" fullWidth>
				<Text variant="h4">
					{currentTicket?.name}
				</Text>
				{
					currentTicket?.responsibleUser && (
						<Text>
							{`${t('responsible')}: ${currentTicket?.responsibleUser}`}
						</Text>
					)
				}
				{
					currentTicket?.priority && (
						<Text>
							{`${t('Priority')}: ${currentTicket?.priority}`}
						</Text>
					)
				}
			</Layout>
			<Layout direction="row" gap="lg" nowrap fullWidth justifyContent="start">
				<Layout direction="column" nowrap>
					<table>
						<tbody>
							<tr>
								<td><StyledNoSelectText>Reporter:</StyledNoSelectText></td>
								<td>{currentTicket?.customer?.reportedByName || ''}</td>
							</tr>
							<tr>
								<td><StyledNoSelectText>Type:</StyledNoSelectText></td>
								<td>{currentTicket?.faultyService?.serviceType}</td>
							</tr>
							<tr>
								<td><StyledNoSelectText>Status:</StyledNoSelectText></td>
								<td>{currentTicket?.status}</td>
							</tr>
						</tbody>
					</table>
				</Layout>
				<Layout direction="column" nowrap>
					<table>
						<tbody>
							<tr>
								<td><StyledNoSelectText>{t('Created')}:</StyledNoSelectText></td>
								<td>{getDateByCustomFormat(ticketList?.createdAt, DATE_FORMATS.DD_MM_YYYY_HH_mm)}</td>
							</tr>
							<tr>
								<td><StyledNoSelectText>{t('Updated')}:</StyledNoSelectText></td>
								<td>{getDateByCustomFormat(currentTicket?.lastUpdate, DATE_FORMATS.DD_MM_YYYY_HH_mm)}</td>
							</tr>
							<tr>
								<td><StyledNoSelectText>{t('Severity')}:</StyledNoSelectText></td>
								<td>{currentTicket?.severity}</td>
							</tr>
						</tbody>
					</table>
				</Layout>
			</Layout>
			<Layout fullWidth gap="sm">
				{/* {
					ticketList?.unifon_subject && (
						<TextDivWithLabel 
							text={ticketList?.unifon_subject}
							label={t('Unifon description')}
						/>
					)
				} */}
				<Layout fullWidth>
					<Accordion 
						title={`${t('Active Email list')}: ${currentTicket?.notificationEmails?.length || 0}/${MAX_NOTIFICATION_EMAILS}`}
						onToggle={toggleEmailAccordion}
						expanded={emailAccordion}
					>
						<Layout
							nowrap
							direction="column"
							fullWidth
							style={{position: 'relative'}}
						>
							<EmailNotificationTable
								data={currentTicket?.notificationEmails || []}
								onDelete={handleDeleteEmail}
							/>
						</Layout>
						<Divider dark style={{marginBottom: '10px'}} />
						<Layout fullWidth nowrap justifyContent="space-between">
							<Layout style={{width: '50%'}}>
								<TextField
									placeholder={t('name.lastname@unifon.no')}
									value={email}
									fullWidth
									onChange={handleEmailChange}
								/>
							</Layout>
							<Layout>
								<Button
									variant="primary"
									color="success"
									onClick={handleAddEmail}
									disabled={!validateEmail(email)}
								>
									{t('Add')}
								</Button>
							</Layout>
						</Layout>
					</Accordion>
				</Layout>
				<Layout fullWidth>
					<Accordion
						title={`${t('Attachments')}: ${attachmentSize}`}
						onToggle={toggleAttachmentAccordion}
						expanded={attachmentAccordion}
					> 
						<Layout
							nowrap
							direction="column"
							fullWidth
							style={{position: 'relative'}}
						>
							{currentTicket && (
								<CaseAttachments data={currentTicket} />
							)}
						</Layout>
					</Accordion>
				</Layout>
			</Layout>
			<StyledDivider />
			<TextDivWithLabel
				secondaryText={currentTicket?.statusChangeReason ?? ''}
				text={currentTicket?.description ?? ''}
				label={t('Description')}
			/>
			<Layout fullWidth>
				<Accordion
					title={`${t('Notes')}: ${currentTicket?.note?.length || 0}`}
					expanded={noteAccordion}
					onToggle={toggleNoteAccordion}
				>
					<Layout
						nowrap
						direction="column"
						fullWidth
						style={{position: 'relative'}}
					>
						<Layout nowrap direction="column" fullWidth>
							<Layout direction="row" fullWidth gap="none">
								<TextArea
									fullWidth
									value={noteMsg}
									onChange={handleNoteChange}
									style={{minHeight: '150px'}}
									placeholder={t('Add case note...')}
								/>
								<Button
									variant="secondary"
									onClick={handleAddNote}
									disabled={!noteMsg.trim()}
								>
									{t('Send')}
								</Button>
							</Layout>
							{
								isNotes && (
									<>
										<Divider dark />
										<Notes
											notesData={currentTicket?.note ?? []}
											userId={isUser ?? ''}
											caseId={currentTicket?.id ? String(currentTicket.id) : ''}
										/>
									</>
								)
							}
						</Layout>
					</Layout>
				</Accordion>
			</Layout>
		</Layout>
	);
}

export default CaseContent;
